import { inject, injectable } from 'inversify';
import { RpcSuccess } from '@dealroadshow/json-rpc-dispatcher';
import type JsonRpcDispatcher from '@dealroadshow/json-rpc-dispatcher';
import Request from '@framework/api/Rpc/Request';
import AskAiDITypes from '@creditflowresearch/socket-client/socket-ask-ai-client/DITypes';
import {
  IAskAiPromptsEntry,
  IAskAiPromptsFields,
  IAskAiSavePromptsResult,
} from '@/creditflowresearch/domain/vo/AskAiPrompts';

const hasError = (result: unknown): result is { error: string } => (
  typeof result === 'object' && result !== null && 'error' in result && typeof result.error === 'string'
);

const throwOnError = (result: unknown): void => {
  if (hasError(result)) {
    throw new Error(result.error);
  }
};

@injectable()
class AskAiPromptsRepository {
  constructor(@inject(AskAiDITypes.JsonRpcDispatcher) private readonly rpc: JsonRpcDispatcher) {}

  getPrompts = async (): Promise<IAskAiPromptsEntry[]> => {
    const request = new Request('cfr.ask_ai.get_prompts', {});
    const response = await this.rpc.call<RpcSuccess>(request);
    const result = response.getResult();

    throwOnError(result);

    return result.items;
  };

  savePrompts = async (prompts: IAskAiPromptsFields): Promise<IAskAiSavePromptsResult> => {
    const request = new Request('cfr.ask_ai.save_prompts', prompts);
    const response = await this.rpc.call<RpcSuccess>(request);
    const result = response.getResult();

    throwOnError(result);

    return result;
  };

  deleteVersion = async (id: string): Promise<void> => {
    const request = new Request('cfr.ask_ai.delete_prompts_version', { id });
    const response = await this.rpc.call<RpcSuccess>(request);

    throwOnError(response.getResult());
  };
}

export default AskAiPromptsRepository;
